import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";

export interface PublicContent {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  type: string;
  sourceUrl: string | null;
  embedUrl: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  sourceAuthor: string | null;
  publishedAt: string | null;
  postcode: string | null;
  region: string | null;
  tags: string[];
  equipmentTags: Array<{
    slug?: string;
    make?: string;
    model?: string;
    tractrsId?: string;
    confirmed?: boolean;
    confidence?: number;
  }>;
  sponsored: boolean;
  sponsorName: string | null;
  featured: boolean;
  // Present only on the content-detail endpoint, and only when the item was
  // submitted by a company that still has promotion enabled.
  submittedByCompany?: {
    slug: string;
    name: string;
    logoUrl: string | null;
  } | null;
}

export interface CompanyProfile {
  company: {
    slug: string;
    name: string;
    description: string | null;
    website: string | null;
    logoUrl: string | null;
  };
  content: PublicContent[];
}

export function useCompanyProfile(slug: string) {
  return useQuery({
    queryKey: ["company-profile", slug],
    queryFn: () => api<CompanyProfile>(`/v1/company/${slug}`),
    enabled: !!slug,
  });
}

export function useContentList(params: {
  page?: number;
  pageSize?: number;
  type?: string;
  postcode?: string;
  region?: string;
  tag?: string;
  equipmentSlug?: string;
}) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, String(v));
  return useQuery({
    queryKey: ["content", "list", params],
    queryFn: () =>
      api<{
        data: PublicContent[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(`/v1/content?${search}`),
    placeholderData: (prev) => prev,
  });
}

export function useContentDetail(slug: string) {
  return useQuery({
    queryKey: ["content", "detail", slug],
    queryFn: () => api<PublicContent>(`/v1/content/${slug}`),
    enabled: !!slug,
  });
}

export function useDirtTalkToday() {
  return useQuery({
    queryKey: ["dirt-talk", "today"],
    queryFn: () =>
      api<{ date: string; items: Array<{ id: string; variant: string; title: string; body: string; imageUrl: string | null }> }>(
        "/v1/dirt-talk/today",
      ),
  });
}

export function useDirtTalkDates() {
  return useQuery({
    queryKey: ["dirt-talk", "dates"],
    queryFn: () => api<{ dates: string[] }>("/v1/dirt-talk/dates"),
  });
}

export function useDirtTalkRecent() {
  return useQuery({
    queryKey: ["dirt-talk", "recent"],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          date: string;
          variant: string;
          title: string;
          body: string;
          imageUrl: string | null;
        }>;
      }>("/v1/dirt-talk/recent"),
  });
}

export function useDirtTalkByDate(date: string) {
  return useQuery({
    queryKey: ["dirt-talk", "by-date", date],
    queryFn: () =>
      api<{ date: string; items: Array<{ id: string; variant: string; title: string; body: string; imageUrl: string | null }> }>(
        `/v1/dirt-talk/${date}`,
      ),
    enabled: !!date,
  });
}

export interface RigOfWeekResponse {
  id: string;
  weekStarting: string;
  operatorName: string | null;
  operatorHandle: string | null;
  description: string | null;
  imageUrl: string | null;
  isSponsored: boolean;
  sponsorName: string | null;
  equipment: {
    make: string | null;
    model: string | null;
    slug: string | null;
    confirmed: boolean;
    machinery: {
      slug: string;
      name: string;
      model: string | null;
      year: number | null;
      brand: { name: string; slug: string } | null;
      category: { name: string; slug: string } | null;
      primaryImageUrl: string | null;
      enginePowerHp: number | null;
      operatingWeightKg: number | null;
      description: string | null;
    } | null;
  } | null;
  sourceContent: {
    slug: string;
    embedUrl: string;
    title: string;
  } | null;
}

export function useRigOfWeek() {
  return useQuery({
    queryKey: ["rig-of-week", "current"],
    queryFn: () => api<null | RigOfWeekResponse>("/v1/rig-of-week/current"),
  });
}

export interface PastRigItem {
  id: string;
  weekStarting: string;
  operatorName: string | null;
  imageUrl: string | null;
  equipment: { make: string | null; model: string | null; slug: string | null } | null;
}

export function useRigOfWeekPast(take = 8) {
  return useQuery({
    queryKey: ["rig-of-week", "past", take],
    queryFn: () =>
      api<{ items: PastRigItem[] }>(
        `/v1/rig-of-week/past?take=${take}&excludeCurrent=true`,
      ),
  });
}

export function useOperator(slug: string) {
  return useQuery({
    queryKey: ["operator", slug],
    queryFn: () =>
      api<{
        operator: { name: string; slug: string; bio: string | null; postcode: string; region: string; avatarUrl: string | null; equipmentSummary: string | null };
        content: PublicContent[];
      }>(`/v1/operators/${slug}`),
    enabled: !!slug,
  });
}

export function usePostcode(postcode: string) {
  return useQuery({
    queryKey: ["postcode", postcode],
    queryFn: () =>
      api<{
        postcode: string;
        profile: { suburb: string; region: string; contentCount?: number; operatorCount?: number };
        content: PublicContent[];
        operators: Array<{ id: string; slug: string; name: string; postcode: string; region: string }>;
      }>(`/v1/postcodes/${postcode}`),
    enabled: !!postcode,
  });
}

export function useEquipment(slug: string) {
  return useQuery({
    queryKey: ["equipment", slug],
    queryFn: () =>
      api<{
        machinery: {
          slug: string;
          name: string;
          model: string | null;
          year: number | null;
          brand: { name: string; slug?: string } | null;
          category: { name: string; slug?: string } | null;
          primaryImageUrl: string | null;
          description: string | null;
          enginePowerHp: number | null;
          operatingWeightKg: number | null;
        };
        content: PublicContent[];
      }>(`/v1/equipment/${slug}`),
    enabled: !!slug,
  });
}

export interface EquipmentSearchHit {
  id: string;
  slug: string;
  name: string;
  model?: string | null;
  brand?: { name: string; slug: string } | null;
}

export function useEquipmentSearch(q: string) {
  return useQuery({
    queryKey: ["equipment", "search", q],
    queryFn: () => api<{ data: EquipmentSearchHit[] }>(`/v1/equipment/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useSubmitQuote() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ ok: true; id: string }>("/v1/quote-requests", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

export interface MagicLinkPayload {
  email: string;
  // Optional JIT signup data — backend creates the user (and Company if
  // accountType=COMPANY) on verify when no user exists for this email.
  name?: string;
  accountType?: "INDIVIDUAL" | "COMPANY";
  company?: { name: string; abn?: string; website?: string };
}

export function useSendMagicLink() {
  return useMutation({
    mutationFn: (input: string | MagicLinkPayload) => {
      const body = typeof input === "string" ? { email: input } : input;
      return api<{ ok: boolean }>("/v1/auth/magic-link", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>("/v1/auth/logout", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth"] }),
  });
}

export interface CronRun {
  id: string;
  name: string;
  status: string;
  durationMs: number;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
}

export function useCronRuns() {
  return useQuery({
    queryKey: ["dashboard", "cron-runs"],
    queryFn: () => api<{ lastRuns: CronRun[] }>("/v1/dashboard/cron-runs"),
    refetchInterval: 60_000,
  });
}

export interface AiCostByType {
  callType: string;
  totalUsd: number;
  count: number;
  totalTokens: number;
}

export function useAiCosts() {
  return useQuery({
    queryKey: ["dashboard", "ai-costs"],
    queryFn: () => api<{ since: string; totalUsd: number; byType: AiCostByType[] }>("/v1/dashboard/ai-costs"),
  });
}

export function useCronRunNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ ok: true }>(`/v1/dashboard/cron/${name}/run-now`, { method: "POST" }),
    onSuccess: () => {
      [2000, 7000, 15000].forEach((delay) =>
        setTimeout(() => qc.invalidateQueries({ queryKey: ["dashboard", "cron-runs"] }), delay),
      );
    },
  });
}
